import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import { ApiError } from "../../api/errors";
import type { DepositHistoryRow, DepositList, RefundableFigures, RefundDetail, RefundList } from "../../api/types";
import { RequireMfa } from "../../auth/guards";
import { CursorListView } from "../../components/CursorListView";
import {
  Banner,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Money,
  PageHeader,
  QueryView,
  RefId,
  Select,
  SkeletonBlock,
  StatusPill,
  Table,
  Textarea,
  type Tone,
} from "../../components/ui";
import { formatLocal } from "../../lib/datetime";
import { newIdempotencyKey } from "../../lib/idempotency";
import { amountValidationMessage, compareDecimal, formatMoney, isZeroDecimal, validateAmountInput } from "../../lib/money";
import { useCursorList } from "../../lib/useCursorList";

// UX-DESIGN.md §2.2 Refunds (migration 0056, refunds.go / wallet.go):
//   GET  /v1/wallet/deposits                    the deposit picker (G-24)
//   GET  /v1/wallet/deposits/{id}/refundable    "up to $X, as of just now" (G-25)
//   POST /v1/refunds {source_deposit_id, amount, reason?} — RequireRecentMFA;
//        rejections are 422 {error, detail} with the fresh figure (G-22)
//   GET  /v1/refunds, GET /v1/refunds/{id}      list and lifecycle (G-26)
// Every refund returns to the original payment method; there is no
// destination field, by design (PLAN.md §6).

const REASON_MAX = 500;

/** The provider_operations lifecycle (§2.2), falling back to refund_state. */
const PROVIDER_STATE: Record<string, { tone: Tone; label: string; detail: string }> = {
  prepared: { tone: "warning", label: "Preparing", detail: "We've recorded your request and are preparing it for our payment provider." },
  submitting: {
    tone: "warning",
    label: "With the payment provider",
    detail: "Submitted to our payment provider. Card refunds usually take 5–10 business days to appear.",
  },
  unknown: { tone: "warning", label: "Confirming", detail: "We're confirming what happened with this refund with our payment provider." },
  confirmed: { tone: "success", label: "Refunded", detail: "Our payment provider confirmed the refund to your original payment method." },
  failed: { tone: "error", label: "Couldn't complete", detail: "" },
  // refund_state values, for a refund with no provider operation row.
  pending: { tone: "warning", label: "Preparing", detail: "We've recorded your request and are preparing it for our payment provider." },
  submitted: {
    tone: "warning",
    label: "With the payment provider",
    detail: "Submitted to our payment provider. Card refunds usually take 5–10 business days to appear.",
  },
};

function refundStatus(r: RefundDetail) {
  // A failed or confirmed refund_state is final whatever the operation says.
  const key = r.state === "failed" || r.state === "confirmed" ? r.state : (r.provider_state ?? r.state);
  return PROVIDER_STATE[key] ?? { tone: "neutral" as Tone, label: key, detail: "" };
}

const FAILURE_REASON: Record<NonNullable<RefundDetail["failure_reason"]>, string> = {
  provider_declined: "our payment provider declined it",
  failed_after_confirmation: "our payment provider reported it failed after first confirming it",
  resolved_failed_by_review: "our team confirmed with the payment provider that it didn't go through",
};

export function RefundsPage() {
  return (
    <div className="stack-lg">
      <PageHeader title="Refunds" description="Refunds always go back to the original payment method, and only ever cover cash you deposited: promotional credit is never refundable." />
      <RequireMfa>
        <RefundForm />
      </RequireMfa>
      <RefundHistoryCard />
    </div>
  );
}

function RefundHistoryCard() {
  const ep = useEndpoints();
  const list = useCursorList<RefundList, RefundDetail>(["refunds"], (cursor) => ep.advertiser.listRefunds(cursor), (p) => p.refunds);
  return (
    <Card title="Refund history">
      <CursorListView list={list} loadingLabel="Loading refunds" empty={<EmptyState icon="undo" title="No refunds requested yet" />}>
        {(rows) => (
          <Table
            caption="Refunds, newest first"
            captionHidden
            rows={rows}
            rowKey={(r) => r.refund_id}
            columns={[
              { key: "at", header: "Requested", cell: (r) => <Link to={`/advertiser/refunds/${r.refund_id}`}>{formatLocal(r.created_at)}</Link> },
              { key: "amt", header: "Amount", cell: (r) => <Money value={r.amount} currency={r.currency} />, numeric: true },
              {
                key: "state",
                header: "Status",
                cell: (r) => {
                  const s = refundStatus(r);
                  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
                },
              },
              { key: "dep", header: "From deposit", cell: (r) => <code>{r.source_deposit_id.slice(0, 8)}</code> },
              { key: "reason", header: "Reason", cell: (r) => r.reason ?? <span className="muted">—</span> },
            ]}
          />
        )}
      </CursorListView>
    </Card>
  );
}

function RefundForm() {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const deposits = useCursorList<DepositList, DepositHistoryRow>(["deposits"], (cursor) => ep.advertiser.listDeposits(cursor), (p) => p.deposits);
  const refundable = deposits.rows.filter((d) => d.deposit_id !== null);
  const [depositId, setDepositId] = useState(params.get("deposit") ?? "");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Key bound to (deposit, amount): same request -> same key -> the server
  // replays instead of refunding twice (submit_refund, 0056). The reason is
  // not part of the server's idempotency check, so it isn't part of ours.
  const [intent, setIntent] = useState<{ sig: string; key: string } | null>(null);

  const figures = useQuery({
    queryKey: ["refundable", depositId],
    queryFn: () => ep.advertiser.getRefundable(depositId),
    enabled: !!depositId,
  });

  const m = useMutation({
    mutationFn: (v: { depositId: string; amount: string; key: string; reason: string }) =>
      ep.advertiser.submitRefund(v.depositId, v.amount, v.key, v.reason || undefined),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ["refunds"] });
      void qc.invalidateQueries({ queryKey: ["wallet"] });
      navigate(`/advertiser/refunds/${res.refund_id}?new=1`);
    },
    onError: (err) => {
      // The rejection carries the fresh figure; refresh the form's too.
      if (err instanceof ApiError && err.detail?.max_refundable !== undefined) void figures.refetch();
    },
  });

  const f = figures.data;
  const locked = !!f?.locked_reason;

  function submit(e: FormEvent) {
    e.preventDefault();
    const fe: Record<string, string> = {};
    if (!depositId) fe.depositId = "Choose the deposit to refund from.";
    const v = validateAmountInput(amount, f?.currency ?? "USD");
    if (!v.ok) fe.amount = amountValidationMessage(v.reason, f?.currency ?? "USD");
    else if (f && compareDecimal(v.value, f.max_refundable) > 0) fe.amount = "That's more than the refundable amount shown above.";
    if ([...reason.trim()].length > REASON_MAX) fe.reason = `Keep the reason to ${REASON_MAX} characters.`;
    setErrors(fe);
    if (Object.keys(fe).length || !v.ok) return;
    const sig = `${depositId}|${v.value}`;
    const key = intent?.sig === sig ? intent.key : newIdempotencyKey();
    setIntent({ sig, key });
    m.mutate({ depositId, amount: v.value, key, reason: reason.trim() });
  }

  return (
    <Card title="Request a refund" subtitle="You'll confirm it's you before it's submitted.">
      <form className="stack" onSubmit={submit} noValidate>
        {deposits.query.isPending ? (
          <SkeletonBlock label="Loading your deposits" lines={1} />
        ) : deposits.query.isError ? (
          <ErrorState error={deposits.query.error} onRetry={() => void deposits.query.refetch()} />
        ) : refundable.length === 0 ? (
          <p className="muted">You don't have a settled deposit to refund from yet.</p>
        ) : (
          <div style={{ maxWidth: 480 }}>
            <Select
              label="Deposit"
              value={depositId}
              placeholder="Choose a deposit"
              onChange={(e) => {
                setDepositId(e.target.value);
                m.reset();
              }}
              error={errors.depositId}
              options={refundable.map((d) => ({
                value: d.deposit_id!,
                label: `${formatLocal(d.settled_at ?? d.created_at)} — ${formatMoney(d.amount, d.currency)} (${d.state})`,
              }))}
            />
            {deposits.hasMore ? (
              <Button variant="ghost" size="sm" onClick={deposits.loadMore} loading={deposits.loadingMore}>
                Show older deposits
              </Button>
            ) : null}
          </div>
        )}

        {depositId ? <RefundableView q={figures} /> : null}

        <div style={{ maxWidth: 320 }}>
          <Input
            label={`Amount (${f?.currency ?? "USD"})`}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={errors.amount}
            disabled={locked}
          />
        </div>
        <Textarea
          label="Reason"
          optional
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={errors.reason}
          hint={`${[...reason.trim()].length}/${REASON_MAX} characters. Kept with the refund for your records and our support team.`}
          disabled={locked}
        />
        <div className="stack-sm">
          <span className="field-label">Refund to</span>
          <span>Your original payment method</span>
          <span className="xs muted">Refunds can't be sent anywhere else. The card's last four digits aren't recorded, so they can't be shown.</span>
        </div>
        {m.error ? <RefundError err={m.error} currency={f?.currency ?? "USD"} /> : null}
        <div>
          <Button type="submit" loading={m.isPending} disabled={locked}>
            Request refund
          </Button>
        </div>
      </form>
    </Card>
  );
}

const LIMITED_BY: Record<RefundableFigures["limited_by"], string> = {
  deposit_ceiling: "Limited by what's left of this deposit after earlier refunds and disputes.",
  spendable_balance: "Limited by your wallet's spendable balance: money already spent on campaigns can't be refunded.",
  dispute_lock: "Locked by an open dispute.",
};

function RefundableView({ q }: { q: UseQueryResult<RefundableFigures> }) {
  return (
    <QueryView query={q} loadingLabel="Checking how much is refundable" lines={2}>
      {(f: RefundableFigures) =>
        f.locked_reason ? (
          <Banner tone="warning" title="This deposit can't be refunded right now">
            {f.lock_detail === "dispute_not_yet_recorded"
              ? "A dispute on this payment has just arrived from the card issuer and is still being recorded. Refunds are blocked until it's reconciled."
              : "A chargeback is open on this payment. Refunds on it are blocked until the dispute is resolved."}
          </Banner>
        ) : (
          <div className="stack-sm">
            <p>
              Up to <strong><Money value={f.max_refundable} currency={f.currency} /></strong> refundable, as of just now (
              {new Date(f.as_of).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}). It's checked again when you submit.
            </p>
            <span className="small muted">{LIMITED_BY[f.limited_by]}</span>
            <Table
              caption="How the refundable amount is worked out"
              rows={[f]}
              rowKey={(r) => r.deposit_id}
              columns={[
                { key: "dep", header: "Deposit", cell: (r) => <Money value={r.ceiling.deposit_amount} currency={r.currency} />, numeric: true },
                { key: "ref", header: "Refunded", cell: (r) => <Money value={r.ceiling.refunded} currency={r.currency} />, numeric: true },
                { key: "fl", header: "Refunds in progress", cell: (r) => <Money value={r.ceiling.in_flight} currency={r.currency} />, numeric: true },
                { key: "dis", header: "Disputed", cell: (r) => <Money value={r.ceiling.disputed} currency={r.currency} />, numeric: true },
                { key: "rem", header: "Left on deposit", cell: (r) => <Money value={r.ceiling.remaining} currency={r.currency} />, numeric: true },
                { key: "sp", header: "Wallet spendable", cell: (r) => <Money value={r.spendable} currency={r.currency} />, numeric: true },
              ]}
            />
          </div>
        )
      }
    </QueryView>
  );
}

/** Typed refund rejections (refunds.go:1-21, submit_refund 0056): the updated figure is shown, not a generic refusal. */
function RefundError({ err, currency }: { err: Error; currency: string }) {
  const api = err instanceof ApiError ? err : null;
  const max = api?.detailString("max_refundable");
  const asOf = api?.detailString("as_of");
  let extra = null;
  if (api?.code === "refund_exceeds_refundable" && max !== undefined) {
    extra = (
      <span>
        Rejected — up to <Money value={max} currency={currency} /> is refundable now
        {asOf ? ` (as of ${new Date(asOf).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })})` : ""}.
      </span>
    );
  } else if (api?.code === "insufficient_spendable") {
    const available = api.detailString("available");
    extra = (
      <span>
        {available !== undefined ? (
          <>
            Your spendable balance is <Money value={available} currency={currency} />
          </>
        ) : null}
        {max !== undefined && !isZeroDecimal(max) ? (
          <>
            ; up to <Money value={max} currency={currency} /> can be refunded now
          </>
        ) : null}
        .
      </span>
    );
  } else if (api?.code === "invalid_amount" && api.detailString("reason") === "sub_cent_precision") {
    extra = <span>Use whole cents.</span>;
  }
  return (
    <Banner tone={api?.kind === "step_up_required" ? "neutral" : "error"} live="alert">
      <div className="stack-sm">
        <span>{err.message}</span>
        {extra}
        {api?.idempotencyKey ? <RefId value={api.idempotencyKey} /> : null}
        {api?.retryable ? (
          <span className="small">
            We can't tell whether this reached the server. Submitting the same deposit and amount again is safe — it's recognized as the
            same request and can't refund twice.
          </span>
        ) : null}
      </div>
    </Banner>
  );
}

export function RefundDetailPage() {
  const { refundId = "" } = useParams();
  const [params] = useSearchParams();
  const ep = useEndpoints();
  const q = useQuery({
    queryKey: ["refund", refundId],
    queryFn: () => ep.advertiser.getRefund(refundId),
    refetchInterval: (query) => {
      const s = query.state.data?.state;
      return s === "pending" || s === "submitted" ? 15_000 : false;
    },
  });
  return (
    <div className="stack-lg">
      <PageHeader title="Refund" back={{ to: "/advertiser/refunds", label: "Refunds" }} description={<code>{refundId}</code>} />
      {params.get("new") === "1" ? (
        <Banner tone="success" title="Refund requested" live="polite">
          The amount is set aside from your spendable balance while our payment provider processes it.
        </Banner>
      ) : null}
      <QueryView query={q} loadingLabel="Loading refund">
        {(r: RefundDetail) => {
          const s = refundStatus(r);
          const pending = r.state === "pending" || r.state === "submitted";
          return (
            <Card>
              <div className="stack">
                <div className="row">
                  <StatusPill tone={s.tone}>{s.label}</StatusPill>
                  {pending ? <span className="xs muted">Updates automatically</span> : null}
                </div>
                <p role="status">
                  {r.state === "failed"
                    ? `We couldn't complete this refund to your original payment method: ${r.failure_reason ? FAILURE_REASON[r.failure_reason] : "the reason wasn't reported"}. Contact support for alternative resolution.`
                    : s.detail}
                </p>
                <dl className="definition-list">
                  <dt>Amount</dt>
                  <dd>
                    <Money value={r.amount} currency={r.currency} />
                  </dd>
                  <dt>Requested</dt>
                  <dd>{formatLocal(r.created_at)}</dd>
                  <dt>From deposit</dt>
                  <dd>
                    <code>{r.source_deposit_id}</code>
                  </dd>
                  <dt>Reason</dt>
                  <dd>{r.reason ?? "None given"}</dd>
                  <dt>Destination</dt>
                  <dd>Your original payment method</dd>
                  <dt>Your wallet</dt>
                  <dd>
                    {r.encumbrance.status === "held"
                      ? "The amount is set aside from your spendable balance while this is in progress."
                      : r.encumbrance.status === "drawn"
                        ? "Paid out of your wallet."
                        : r.encumbrance.status === "released"
                          ? "The set-aside amount went back to your spendable balance."
                          : "Nothing was set aside for this refund."}
                  </dd>
                  {r.provider_status ? (
                    <>
                      <dt>Provider status</dt>
                      <dd>
                        <code>{r.provider_status}</code>
                      </dd>
                    </>
                  ) : null}
                </dl>
                {r.state === "failed" ? (
                  <p className="xs muted">Our payment provider's own decline message isn't stored, so it can't be shown here.</p>
                ) : null}
              </div>
            </Card>
          );
        }}
      </QueryView>
    </div>
  );
}
